import { Divider } from '@chakra-ui/react';
import {
  faWarning,
  faCheck,
  faClockFour,
  faMemoCircleCheck
} from '@fortawesome/pro-regular-svg-icons';
import HighlightItem from '../PeerReview/HighlightItem';
import { SUBMISSION_DETAIL_TABS } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import { useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { OverwriteReviewsModal } from './OverwriteReviewsModal';
import { ButtonText } from '@morressier/alembic';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';

interface CriticalDeadlineReviewsProps {
  criticalDeadlineReviews: number;
  expiredReviews: number;
}

export const CriticalDeadlineReviews = ({
  criticalDeadlineReviews,
  expiredReviews
}: CriticalDeadlineReviewsProps) => {
  const { setActiveTab } = useSubmissionDetails();
  const totalAlerts = criticalDeadlineReviews + expiredReviews;

  if (totalAlerts > 0) {
    const contextParts = [];
    if (expiredReviews > 0) {
      contextParts.push(`${expiredReviews} Expired Deadline`);
    }
    if (criticalDeadlineReviews > 0) {
      contextParts.push(`${criticalDeadlineReviews} Approaching Deadlines`);
    }
    return (
      <>
        <Divider />
        <HighlightItem
          variant="caution"
          icon={faWarning}
          heading={`${totalAlerts} Deadline Alerts`}
          context={contextParts.join(', ')}
          buttonOnClick={() => setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)}
          buttonText="View Reviews"
        />
      </>
    );
  }

  return null;
};

export const CompletedReviews = ({
  minReviews,
  completedReviews
}: {
  minReviews?: number;
  completedReviews: number;
}) => {
  const [minReviewsModalOpen, setMinReviewsModalOpen] = useState(false);
  const {
    refetchSubmission,
    submissionContextId,
    submissionId,
    organizationId,
    workflowResponse
  } = useSubmissionData();
  const defaultMinReviews = workflowResponse?.workflowData?.minReviews ?? 2;

  const handleCloseModal = () => {
    setMinReviewsModalOpen(false);
  };

  return (
    <>
      {minReviews && completedReviews < minReviews ? (
        <HighlightItem
          variant="attention"
          icon={faMemoCircleCheck}
          customButton={
            <ProtectedWorkflowAction actionName="SetMinReviewCount">
              <ButtonText
                flexShrink="0"
                onClick={() => setMinReviewsModalOpen(true)}
              >
                Overwrite Target
              </ButtonText>
            </ProtectedWorkflowAction>
          }
          heading={`${minReviews} Reviews ${
            minReviews === defaultMinReviews ? '(Default Value)' : ''
          }`}
          context={`A complete evaluation aims for at least ${minReviews} reviews – decisions can proceed with fewer reviews. Adjustments affect queue automation.`}
        />
      ) : (
        <HighlightItem
          variant="success"
          icon={faCheck}
          customButton={
            <ProtectedWorkflowAction actionName="SetMinReviewCount">
              <ButtonText
                flexShrink="0"
                onClick={() => setMinReviewsModalOpen(true)}
              >
                Overwrite Target
              </ButtonText>
            </ProtectedWorkflowAction>
          }
          heading={`${minReviews} Reviews (Target Met)`}
          context={`A complete evaluation has been achieved with ${minReviews} reviews. Proceed with your decision, or wait for additional reviews if desired. Adjustments affect queue automation.`}
        />
      )}

      {minReviewsModalOpen && (
        <OverwriteReviewsModal
          handleCloseModal={handleCloseModal}
          defaultMinReviews={defaultMinReviews}
          refetchSubmission={refetchSubmission}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          submissionId={submissionId}
        />
      )}
    </>
  );
};

export const ReviewsDeadlineRequested = ({
  deadlineExtensionRequests
}: {
  deadlineExtensionRequests: number;
}) => {
  const { setActiveTab } = useSubmissionDetails();

  return (
    <>
      {deadlineExtensionRequests > 0 && (
        <>
          <Divider />
          <HighlightItem
            variant="attention"
            icon={faClockFour}
            heading={`${deadlineExtensionRequests} Deadline Extension Requested`}
            buttonOnClick={() => setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)}
            buttonText="View Reviews"
          />
        </>
      )}
    </>
  );
};
